export class Translator {
    constructor(locale) {
        this.messages = {};
        this.sPluralRegex = new RegExp(/^\w+\: +(.+)$/);
        this.cPluralRegex = new RegExp(/^\s*((\{\s*(\-?\d+[\s*,\s*\-?\d+]*)\s*\})|([\[\]])\s*(-Inf|\-?\d+)\s*,\s*(\+?Inf|\-?\d+)\s*([\[\]]))\s?(.+?)$/);
        this.iPluralRegex = new RegExp(/^\s*(\{\s*(\-?\d+[\s*,\s*\-?\d+]*)\s*\})|([\[\]])\s*(-Inf|\-?\d+)\s*,\s*(\+?Inf|\-?\d+)\s*([\[\]])/);
        this.setLocale = (locale) => {
            this.locale = locale;
        };
        this.getLocale = () => {
            return this.locale;
        };
        this.load = (locale, translations) => {
            this.messages[locale] = translations;
            this.locale = locale;
        };
        this.replaceParameters = (message, parameters) => {
            Object.keys(parameters).forEach(parameter => {
                const value = parameters[parameter];
                if (message) {
                    message = message.replace(new RegExp("%" + parameter + "%", "g"), value);
                }
            });
            return message;
        };
        this.hasLocale = (locale) => {
            return this.messages.hasOwnProperty(locale);
        };
        this.hasMessage = (id, messages) => {
            return messages.hasOwnProperty(id);
        };
        this.getMessage = (id, count, messages, locale) => {
            let message = messages[id];
            if (!isNaN(count)) {
                message = this.pluralize(message, count, locale);
            }
            return message;
        };
        /**
         * Convert number as String, "Inf" and "-Inf"
         * values to number values.
         */
        this.convertNumber = function (number) {
            if ("-Inf" === number) {
                return Number.NEGATIVE_INFINITY;
            }
            else if ("+Inf" === number || "Inf" === number) {
                return Number.POSITIVE_INFINITY;
            }
            return parseInt(number, 10);
        };
        /**
         * Given a message with different plural translations separated by a
         * pipe (|), this method returns the correct portion of the message based
         * on the given number, the current locale and the pluralization rules
         * in the message itself.
         *
         * The message supports two different types of pluralization rules:
         *
         * interval: {0} There is no apples|{1} There is one apple|]1,Inf] There is %count% apples
         * indexed:  There is one apple|There is %count% apples
         *
         * The indexed solution can also contain labels (e.g. one: There is one apple).
         * This is purely for making the translations more clear - it does not
         * affect the functionality.
         *
         * The two methods can also be mixed:
         *     {0} There is no apples|one: There is one apple|more: There is %count% apples
         */
        this.pluralize = (message, number, locale) => {
            const explicitRules = [];
            const standardRules = [];
            const parts = message.split("|");
            let matches = [];
            for (let p = 0; p < parts.length; p++) {
                var part = parts[p];
                if (this.cPluralRegex.test(part)) {
                    matches = part.match(this.cPluralRegex);
                    explicitRules[matches[0]] = matches[matches.length - 1];
                }
                else if (this.sPluralRegex.test(part)) {
                    matches = part.match(this.sPluralRegex);
                    standardRules.push(matches[1]);
                }
                else {
                    standardRules.push(part);
                }
            }
            for (let e in explicitRules) {
                if (this.iPluralRegex.test(e)) {
                    matches = e.match(this.iPluralRegex);
                    if (matches[1]) {
                        const ns = matches[2].split(",");
                        for (let n in ns) {
                            if (number == ns[n]) {
                                return explicitRules[e];
                            }
                        }
                    }
                    else {
                        const leftNumber = this.convertNumber(matches[4]);
                        const rightNumber = this.convertNumber(matches[5]);
                        if (("[" === matches[3] ? number >= leftNumber : number > leftNumber) &&
                            ("]" === matches[6] ? number <= rightNumber : number < rightNumber)) {
                            return explicitRules[e];
                        }
                    }
                }
            }
            return standardRules[this.pluralPosition(number, locale)] || standardRules[0] || undefined;
        };
        /**
         * Returns the plural position to use for the given locale and number.
         */
        this.pluralPosition = (number, locale) => {
            let _locale = locale;
            if ("pt_BR" === _locale) {
                _locale = "xbr";
            }
            if (_locale.length > 3) {
                _locale = _locale.split("_")[0];
            }
            switch (_locale) {
                case "bo":
                case "dz":
                case "id":
                case "ja":
                case "jv":
                case "ka":
                case "km":
                case "kn":
                case "ko":
                case "ms":
                case "th":
                case "tr":
                case "vi":
                case "zh":
                    return 0;
                case "af":
                case "az":
                case "bn":
                case "bg":
                case "ca":
                case "da":
                case "de":
                case "el":
                case "en":
                case "eo":
                case "es":
                case "et":
                case "eu":
                case "fa":
                case "fi":
                case "fo":
                case "fur":
                case "fy":
                case "gl":
                case "gu":
                case "ha":
                case "he":
                case "hu":
                case "is":
                case "it":
                case "ku":
                case "lb":
                case "ml":
                case "mn":
                case "mr":
                case "nah":
                case "nb":
                case "ne":
                case "nl":
                case "nn":
                case "no":
                case "om":
                case "or":
                case "pa":
                case "pap":
                case "ps":
                case "pt":
                case "so":
                case "sq":
                case "sv":
                case "sw":
                case "ta":
                case "te":
                case "tk":
                case "ur":
                case "zu":
                    return number == 1 ? 0 : 1;
                case "am":
                case "bh":
                case "fil":
                case "fr":
                case "gun":
                case "hi":
                case "ln":
                case "mg":
                case "nso":
                case "xbr":
                case "ti":
                case "wa":
                    return number === 0 || number == 1 ? 0 : 1;
                case "be":
                case "bs":
                case "hr":
                case "ru":
                case "sr":
                case "uk":
                    return number % 10 == 1 && number % 100 != 11
                        ? 0
                        : number % 10 >= 2 && number % 10 <= 4 && (number % 100 < 10 || number % 100 >= 20)
                            ? 1
                            : 2;
                case "cs":
                case "sk":
                    return number == 1 ? 0 : number >= 2 && number <= 4 ? 1 : 2;
                case "ga":
                    return number == 1 ? 0 : number == 2 ? 1 : 2;
                case "lt":
                    return number % 10 == 1 && number % 100 != 11
                        ? 0
                        : number % 10 >= 2 && (number % 100 < 10 || number % 100 >= 20)
                            ? 1
                            : 2;
                case "sl":
                    return number % 100 == 1 ? 0 : number % 100 == 2 ? 1 : number % 100 == 3 || number % 100 == 4 ? 2 : 3;
                case "mk":
                    return number % 10 == 1 ? 0 : 1;
                case "mt":
                    return number == 1
                        ? 0
                        : number === 0 || (number % 100 > 1 && number % 100 < 11)
                            ? 1
                            : number % 100 > 10 && number % 100 < 20
                                ? 2
                                : 3;
                case "lv":
                    return number === 0 ? 0 : number % 10 == 1 && number % 100 != 11 ? 1 : 2;
                case "pl":
                    return number == 1
                        ? 0
                        : number % 10 >= 2 && number % 10 <= 4 && (number % 100 < 12 || number % 100 > 14)
                            ? 1
                            : 2;
                case "cy":
                    return number == 1 ? 0 : number == 2 ? 1 : number == 8 || number == 11 ? 2 : 3;
                case "ro":
                    return number == 1 ? 0 : number === 0 || (number % 100 > 0 && number % 100 < 20) ? 1 : 2;
                case "ar":
                    return number === 0
                        ? 0
                        : number == 1
                            ? 1
                            : number == 2
                                ? 2
                                : number >= 3 && number <= 10
                                    ? 3
                                    : number >= 11 && number <= 99
                                        ? 4
                                        : 5;
                default:
                    return 0;
            }
        };
        this.transChoice = (id, number, parameters, domain, locale) => {
            const _locale = locale ? locale : this.locale;
            let message = id;
            if (!this.hasLocale(_locale)) {
                return id;
            }
            const messages = this.messages[_locale];
            if (this.hasMessage(id, messages)) {
                message = this.getMessage(id, number, messages, _locale);
                if (parameters && Object.keys(parameters).length > 0) {
                    message = this.replaceParameters(message, parameters);
                }
            }
            return message;
        };
        this.setLocale(locale);
    }
}
Translator.register = (window, translator) => {
    window._ = (id, number, parameters, domain = null, locale = null) => {
        return translator.transChoice(id, number, parameters, domain, locale);
    };
};
export function _(id, number = undefined, parameters = {}, domain = null, locale = null) {
    return window._(id, number, parameters, domain, locale);
}
